import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const OFFICIAL_LOGO = '/logos/police-mn-logo.png';

function BrandLogo() {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="mp-sidebar__logo" title="LOGO">
        <i className="bi bi-shield-shaded"></i>
      </div>
    );
  }
  return (
    <img
      className="mp-sidebar__logoimg"
      src={OFFICIAL_LOGO}
      alt="Цагдаагийн Ерөнхий Газар"
      onError={() => setFailed(true)}
    />
  );
}

const PAGE_TO_PATH: Record<string, string> = {
  dashboard: '/',
  case: '/case',
  relation: '/relation',
  geo: '/geo',
  evidence: '/evidence',
  substance: '/substance',
  roles: '/roles',
  audit: '/audit',
  forensicreq: '/forensicreq',
  report: '/report',
};

const PATH_TO_PAGE: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_TO_PATH).map(([k, v]) => [v, k])
);

const PARENT_OF: Record<string, string> = {
  case: 'invest',
  evidence: 'invest',
  relation: 'analysis',
  geo: 'analysis',
  substance: 'drugdata',
  roles: 'system',
  audit: 'system',
  forensicreq: 'forensic',
  report: 'forensic',
};

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const page = PATH_TO_PAGE[location.pathname] || 'dashboard';

  const menu = [
    { id: 'dashboard', icon: 'bi-speedometer2', label: t('mDashboard'), children: [], active: true },
    {
      id: 'invest', icon: 'bi-folder2-open', label: t('mInvest'),
      children: [
        { id: 'case', label: t('mCase'), active: true },
        { id: 'subject', label: t('mSubject'), active: false },
        { id: 'evidence', label: t('mEvidence'), active: true },
        { id: 'request', label: t('mRequest'), active: false },
      ],
    },
    {
      id: 'drugdata', icon: 'bi-database', label: t('mDrugData'),
      children: [
        { id: 'substance', label: t('mSubstance'), active: true },
        { id: 'spectrum', label: t('mSpectrum'), active: false },
        { id: 'fielddata', label: t('mFieldData'), active: false },
        { id: 'dataset', label: t('mDataset'), active: false },
        { id: 'model', label: t('mModel'), active: false },
      ],
    },
    {
      id: 'analysis', icon: 'bi-diagram-3', label: t('mAnalysis'),
      children: [
        { id: 'relation', label: t('mRelation'), active: true },
        { id: 'geo', label: t('mGeo'), active: true },
        { id: 'statanalysis', label: t('mStatAnalysis'), active: false },
      ],
    },
    {
      id: 'forensic', icon: 'bi-cpu', label: t('mForensic'),
      children: [
        { id: 'forensicreq', label: t('mForensicReq'), active: true },
        { id: 'digitalevidence', label: t('mDigitalEvidence'), active: false },
        { id: 'report', label: t('mReport'), active: true },
      ],
    },
    {
      id: 'agency', icon: 'bi-building', label: t('mAgency'),
      children: [
        { id: 'otheragency', label: t('mOtherAgency'), active: false },
        { id: 'migration', label: t('mMigration'), active: false },
      ],
    },
    {
      id: 'stats', icon: 'bi-bar-chart', label: t('mStats'),
      children: [
        { id: 'invstats', label: t('mInvStats'), active: false },
        { id: 'reports', label: t('mReports'), active: false },
      ],
    },
    {
      id: 'system', icon: 'bi-gear', label: t('mSystem'),
      children: [
        { id: 'roles', label: t('mRoles'), active: true },
        { id: 'audit', label: t('mAudit'), active: true },
      ],
    },
  ];

  const currentParent = PARENT_OF[page] ?? 'invest';
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const isMenuOpen = (id: string): boolean =>
    overrides[id] !== undefined ? overrides[id] : id === currentParent;
  const toggleMenu = (id: string): void =>
    setOverrides((prev) => ({ ...prev, [id]: !isMenuOpen(id) }));

  return (
    <nav className="mp-sidebar">
      <div className="mp-sidebar__brand" onClick={() => navigate('/')}>
        <BrandLogo />
        <div className="mp-sidebar__brandtext">
          <strong>{t('agency')}</strong>
          <span>{t('agencySub')}</span>
        </div>
      </div>
      <div className="mp-sidebar__system">{t('system')}</div>
      <ul className="mp-sidebar__menu">
        {menu.map((m) => {
          const hasKids = m.children.length > 0;
          const isOpen = isMenuOpen(m.id);
          const childActive = m.children.some((c) => c.id === page);
          const topActive = !hasKids && page === m.id;
          return (
            <li key={m.id}>
              <button type="button"
                className={'mp-sidebar__item' + (childActive || topActive ? ' is-active' : '')}
                onClick={() => {
                  if (hasKids) {
                    toggleMenu(m.id);
                  } else if (m.active && PAGE_TO_PATH[m.id]) {
                    navigate(PAGE_TO_PATH[m.id]);
                  }
                }}>
                <i className={'bi ' + m.icon}></i>
                <span className="mp-sidebar__label">{m.label}</span>
                {hasKids ? <i className={'bi mp-sidebar__chev ' + (isOpen ? 'bi-chevron-up' : 'bi-chevron-down')}></i> : null}
              </button>
              {hasKids && isOpen ? (
                <ul className="mp-sidebar__sub">
                  {m.children.map((c) => (
                    <li key={c.id}>
                      <button type="button"
                        className={'mp-sidebar__subitem' + (page === c.id ? ' is-current' : '') + (c.active ? '' : ' is-disabled')}
                        onClick={() => { if (c.active && PAGE_TO_PATH[c.id]) navigate(PAGE_TO_PATH[c.id]); }}>
                        {c.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
